import { Box, InputBase, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { OverlayLabel } from '~/containers/viewer/viewport/ViewportOverlay/OverlayLabel';

const ClickableValue = styled(OverlayLabel)`
    cursor: text;
    user-select: none;
    min-width: 32px;

    &:hover {
        text-decoration: underline;
    }
`;

/**
 * Edit-mode wrapper used when a unit suffix is shown so the input and the
 * suffix render side-by-side at the same baseline.
 */
const EditWrapper = styled(Box)`
    display: inline-flex;
    align-items: baseline;
    min-width: 32px;
`;

const ValueInput = styled(InputBase)`
    font-size: 11px;
    line-height: 11px;
    font-variant-numeric: tabular-nums;
    font-family: 'Roboto Mono';
    color: inherit;
    background: transparent;
    & input {
        padding: 0;
        height: 11px;
        line-height: 11px;
        background: transparent;
        width: 5ch;
        -moz-appearance: textfield;
        // Match the display label's left-alignment so the digit doesn't
        // shift horizontally when the user clicks into edit mode.
        text-align: left;
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
`;

/**
 * Suffix-mode input variant: narrower so the input followed by the suffix
 * span occupies roughly the same width as the display label.
 */
const SuffixedValueInput = styled(ValueInput)`
    & input {
        width: 3ch;
    }
`;

export interface EditableNumericValueProps {
    /** Current numeric value (in display units). */
    value: number;
    /**
     * Called once when the user commits a parsed, validated, distinct value.
     * The component does not call this on blur/Enter if validation fails or
     * the value is unchanged — see `validate` and the equality check below.
     */
    onCommit: (newValue: number) => void;
    /**
     * Optional validation hook. Returning `false` rejects the input — the
     * component reverts to the prior value without calling `onCommit`.
     * If omitted, any finite parsed number is accepted.
     */
    validate?: (newValue: number) => boolean;
    /** Render-only formatter for display mode. Defaults to `String(value)`. */
    formatDisplay?: (value: number) => string;
    /** Accessible label for the underlying input. */
    ariaLabel?: string;
    /** Optional native HTML5 hints for the number input. */
    min?: number;
    max?: number;
    step?: number;
    /** Optional test id forwarded to both display + edit elements. */
    testId?: string;
    /** Suffix appended after the value in display mode (e.g. ' %'). */
    unitSuffix?: string;
    /**
     * Called when the user enters edit mode. Use this to suppress external
     * state updates from clobbering the buffered string mid-edit if needed.
     */
    onEditStart?: () => void;
    /** Called when the user exits edit mode (commit or cancel). */
    onEditEnd?: () => void;
    /**
     * If true, mount directly in edit mode. Useful when the parent already
     * detected a click (e.g. after a drag-vs-click gesture filter) and wants
     * the user to start typing immediately without an extra click.
     */
    defaultEditing?: boolean;
}

/**
 * Click-to-edit numeric value with Enter/blur commit and Escape cancel.
 *
 * Extracted from the windowing-controls editable WW/WC field (see
 * `WindowingControls.tsx`). The pointer-lock vertical-drag gesture from that
 * component is intentionally NOT included here — this component is a pure
 * keyboard / click-to-edit input.
 *
 * Validation must happen in the caller via `validate`. The component will not
 * silently clamp values; it reverts to the prior value on validation failure
 * so the user always sees a known-good number.
 */
const EditableNumericValue = ({
    value,
    onCommit,
    validate,
    formatDisplay,
    ariaLabel,
    min,
    max,
    step,
    testId,
    unitSuffix = '',
    onEditStart,
    onEditEnd,
    defaultEditing = false,
}: EditableNumericValueProps) => {
    const [isEditing, setIsEditing] = useState(defaultEditing);
    const [inputValue, setInputValue] = useState(String(value));

    // Keep the buffered string in sync with the prop while NOT editing. We
    // intentionally avoid stomping the user's in-flight buffer if a parallel
    // state update lands during an edit (mirrors the WW/WC behaviour).
    useEffect(() => {
        if (!isEditing) {
            setInputValue(String(value));
        }
    }, [value, isEditing]);

    const enterEditMode = (): void => {
        setInputValue(String(value));
        setIsEditing(true);
        onEditStart?.();
    };

    const exitEditMode = (): void => {
        setIsEditing(false);
        onEditEnd?.();
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setInputValue(e.target.value);
    };

    const handleInputBlur = (): void => {
        const newValue = parseFloat(inputValue);
        if (
            !isNaN(newValue) &&
            isFinite(newValue) &&
            newValue !== value &&
            (!validate || validate(newValue))
        ) {
            onCommit(newValue);
        }
        exitEditMode();
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            handleInputBlur();
        } else if (e.key === 'Escape') {
            setInputValue(String(value));
            exitEditMode();
        }
    };

    if (isEditing) {
        const inputProps = {
            value: inputValue,
            onChange: handleInputChange,
            onBlur: handleInputBlur,
            onKeyDown: handleKeyDown,
            autoFocus: true,
            type: 'number',
            inputProps: {
                'aria-label': ariaLabel,
                'data-testid': testId,
                inputMode: 'decimal' as const,
                min,
                max,
                step,
                onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                    e.target.select(),
            },
        };
        if (unitSuffix) {
            return (
                <EditWrapper>
                    <SuffixedValueInput {...inputProps} />
                    <OverlayLabel as="span">{unitSuffix}</OverlayLabel>
                </EditWrapper>
            );
        }
        return <ValueInput {...inputProps} />;
    }

    const displayText = formatDisplay
        ? formatDisplay(value)
        : `${String(value)}${unitSuffix}`;

    return (
        <ClickableValue
            onClick={enterEditMode}
            data-testid={testId}
            aria-label={ariaLabel}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    enterEditMode();
                }
            }}
        >
            {displayText}
        </ClickableValue>
    );
};

export default EditableNumericValue;
